import { type AthleticsLiveFeed, type AthleticsRunningTime, athleticsToBenchSnapshot } from "./timing-athletics";
import type { BenchSnapshot, TimingBenchKind } from "./timing-bench";
import { type AthleticsFieldDisciplineId, type AthleticsFieldFeed, athleticsFieldToBenchSnapshot } from "./timing-field";
import { type Pt8000Feed, pt8000ToBenchSnapshot } from "./timing-pt8000";

/**
 * Reading a venue timer on the LAN → one bench snapshot, for EVERY relay:
 * the Node bridges (`apps/api/scripts/swim-bench-bridge.ts`,
 * `athletics-bench-bridge.ts`) and OVR relaying from the operator's own
 * browser (`apps/ovr/src/families/timed/bench-relay.ts`; Chrome / Edge let an
 * HTTPS page fetch a plain-HTTP private IP or `.local` name once the operator
 * allows "devices on your local network"). Fetch only, no Node APIs.
 *
 *   POOL  — the Seiko PT-8000 race table (`/api/tsr/swimming/results`), converted by `pt8000ToBenchSnapshot`
 *   TRACK — the results console (`/api/athletics/running-time`, required) + the live race
 *           (`/api/feed/live`, a decoration: down = relayed without reactions), by `athleticsToBenchSnapshot`
 *   FIELD — one jump / throw competition (`/api/athletics/field/<discipline>`, the athletics
 *           simulator's — no real field console seen yet), by `athleticsFieldToBenchSnapshot`
 *
 * A timer that does not answer throws `TimerUnavailable` (retry, keep the
 * last table). Nothing here PUTs or applies anything: the relay PUTs the
 * snapshot (`PUT /timing/benches/:id`, Redis only) and the operator applies.
 */

/** No answer from the timer (network, timeout, non-2xx) — not a verdict: retry. */
export class TimerUnavailable extends Error {}

/** The timer's paths. A POOL address with no path gets `POOL_TIMER_PATH`; TRACK paths hang off the base. */
export const POOL_TIMER_PATH = "/api/tsr/swimming/results";
export const TRACK_TIMER_RUNNING_TIME_PATH = "/api/athletics/running-time";
export const TRACK_TIMER_LIVE_PATH = "/api/feed/live";
/** + `/<discipline>` (`ATHLETICS_FIELD_DISCIPLINE_IDS`). */
export const FIELD_TIMER_PATH = "/api/athletics/field";
export const TIMER_READ_TIMEOUT_MS = 3000;
/** Defaults on the venue LAN (host:port), as the bridges and OVR prefill them. */
export const TIMER_DEFAULT_ADDRESS: Readonly<Record<TimingBenchKind, string>> = { POOL: "192.168.0.134:8791", TRACK: "192.168.0.134:8793", FIELD: "192.168.0.134:8793" };
export const TIMER_DEFAULT_BENCH_ID: Readonly<Record<TimingBenchKind, string>> = { POOL: "POOL-1", TRACK: "TRACK-1", FIELD: "FIELD-1" };

const FIELD_BENCH_SUFFIX: Readonly<Record<AthleticsFieldDisciplineId, string>> = {
	high: "HJ",
	pole: "PV",
	long: "LJ",
	triple: "TJ",
	shot: "SP",
	discus: "DT",
	hammer: "HT",
	javelin: "JT",
};

/**
 * A field console's default bench name, per discipline (`FIELD-HJ`,
 * `FIELD-SP`; user, 2026-11-02): two pits running at once never share a name
 * unless someone types one. Unknown → `FIELD-1`. Two pits of ONE discipline
 * (men's and women's long jump together) still rename one by hand.
 */
export function fieldDefaultBenchId(discipline: string | null | undefined): string {
	const suffix = discipline ? FIELD_BENCH_SUFFIX[discipline as AthleticsFieldDisciplineId] : undefined;
	return suffix ? `FIELD-${suffix}` : TIMER_DEFAULT_BENCH_ID.FIELD;
}

/**
 * A typed timer address → its base URL: trimmed, `http://` added when no
 * scheme is given, trailing slashes stripped. null = not a usable http(s) URL.
 */
export function normalizeTimerAddress(input: string): string | null {
	const raw = input.trim();
	if (raw === "") return null;
	const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `http://${raw}`;
	const m = URL_SHAPE.exec(withScheme);
	if (!m?.[2] || m[2].startsWith(":")) return null;
	return withScheme.replace(/\/+$/, "");
}

/** scheme, host[:port], the rest (path / query). No `URL`: contracts compile without DOM / Node types. */
const URL_SHAPE = /^(https?):\/\/([^/?#\s@]+)((?:[/?#][^\s]*)?)$/i;

/** The address names a path of its own (beyond `/`). */
const namesPath = (base: string): boolean => {
	const rest = URL_SHAPE.exec(base.trim())?.[3] ?? "";
	return !(rest === "" || /^\/+$/.test(rest));
};

/** The URL a POOL timer is read at: the address as given when it names a path, else the PT-8000 results path. */
export function poolTimerUrl(base: string): string {
	return namesPath(base) ? base.trim() : `${base.trim().replace(/\/+$/, "")}${POOL_TIMER_PATH}`;
}

/** The URL a FIELD console is read at: the address as given when it names a path, else the discipline's competition. null = no path and no discipline. */
export function fieldTimerUrl(base: string, discipline: string | null | undefined): string | null {
	if (namesPath(base)) return base.trim();
	if (!discipline) return null;
	return `${base.trim().replace(/\/+$/, "")}${FIELD_TIMER_PATH}/${encodeURIComponent(discipline)}`;
}

/** The content, without the read time: an idle timer is not resent (a running race clock is a change). */
export const benchSnapshotKey = (snapshot: BenchSnapshot): string => JSON.stringify({ ...snapshot, captured_at: null });

// ─── Fetch, typed locally (contracts compile without DOM / Node types) ─────────

interface TimerFetchInit {
	headers?: Record<string, string>;
	cache?: "no-store";
	signal?: unknown;
}
interface TimerFetchResponse {
	ok: boolean;
	status: number;
	headers: { get(name: string): string | null };
	json(): Promise<unknown>;
}
export type TimerFetch = (url: string, init: TimerFetchInit) => Promise<TimerFetchResponse>;

const platform = globalThis as unknown as { fetch?: TimerFetch; AbortSignal?: { timeout?: (ms: number) => unknown } };

export interface TimerReaderOptions {
	/**
	 * Node bridges: send `accept` and the PT-8000's ETag (`if-none-match`), so an
	 * unchanged table is a 304 (read → null). Off in the browser: a plain GET
	 * with `cache: "no-store"` stays a CORS simple request (no preflight the
	 * timer may not answer).
	 */
	conditional?: boolean;
	/** Progress lines (reset seen, live feed down / back, race changed). */
	log?: (level: "INFO" | "WARN", message: string) => void;
	/** Defaults to the platform's fetch. */
	fetch?: TimerFetch;
	timeoutMs?: number;
	/** FIELD: the competition to read (`ATHLETICS_FIELD_DISCIPLINE_IDS`), when the address names no path. */
	fieldDiscipline?: string | null;
}

export interface TimerReader {
	/**
	 * Reads the timer once: the snapshot, or null = unchanged since the last
	 * read (ETag, POOL with `conditional`). Throws `TimerUnavailable` when the
	 * timer does not answer. `url` = the address (POOL: the results URL or its
	 * base; TRACK: the base; FIELD: the competition's URL or the base + `fieldDiscipline`).
	 * `capturedAt` is the ISO time to stamp.
	 */
	read(url: string, capturedAt: string): Promise<BenchSnapshot | null>;
}

/** One reader per timer: it remembers the ETag, the reset count, the live feed's state and the race (for the log). */
export function createTimerReader(kind: TimingBenchKind, opts: TimerReaderOptions = {}): TimerReader {
	const log = opts.log ?? (() => {});
	const timeoutMs = opts.timeoutMs ?? TIMER_READ_TIMEOUT_MS;

	const get = async (url: string, what: string, extra: Record<string, string> = {}): Promise<TimerFetchResponse> => {
		const init: TimerFetchInit = opts.conditional ? { headers: { accept: "application/json", ...extra } } : { cache: "no-store" };
		const timeout = platform.AbortSignal?.timeout;
		if (timeout) init.signal = timeout.call(platform.AbortSignal, timeoutMs);
		let res: TimerFetchResponse;
		try {
			if (opts.fetch) res = await opts.fetch(url, init);
			else if (platform.fetch) res = await platform.fetch(url, init);
			else throw new Error("fetch is not available");
		} catch (err) {
			throw new TimerUnavailable(`${what}: ${(err as Error).message}`);
		}
		if (res.status === 304) return res;
		if (!res.ok) throw new TimerUnavailable(`${what}: HTTP ${res.status}`);
		return res;
	};
	const json = async <T>(res: TimerFetchResponse, what: string): Promise<T> => {
		try {
			return (await res.json()) as T;
		} catch (err) {
			throw new TimerUnavailable(`${what}: not JSON (${(err as Error).message})`);
		}
	};

	if (kind === "POOL") {
		let etag: string | null = null;
		let etagUrl: string | null = null;
		let resets: number | null = null;
		return {
			async read(url, capturedAt) {
				const target = poolTimerUrl(url);
				if (target !== etagUrl) {
					etag = null;
					etagUrl = target;
				}
				const res = await get(target, "bench", opts.conditional && etag ? { "if-none-match": etag } : {});
				if (res.status === 304) return null;
				const feed = await json<Pt8000Feed>(res, "bench");
				etag = res.headers.get("etag");
				if (typeof feed.resets === "number") {
					if (resets !== null && feed.resets > resets) log("INFO", `bench reset (${resets} → ${feed.resets}): a new race on the timer`);
					resets = feed.resets;
				}
				return pt8000ToBenchSnapshot(feed, capturedAt);
			},
		};
	}

	if (kind === "FIELD") {
		let lastRace = "";
		return {
			async read(url, capturedAt) {
				const target = fieldTimerUrl(url, opts.fieldDiscipline);
				if (target === null) throw new TimerUnavailable("field console: no competition to read (name a discipline, or give its full URL)");
				const feed = await json<AthleticsFieldFeed>(await get(target, "field console"), "field console");
				let snapshot: BenchSnapshot;
				try {
					snapshot = athleticsFieldToBenchSnapshot(feed, capturedAt);
				} catch (err) {
					throw new TimerUnavailable(`field console: ${(err as Error).message}`);
				}
				if (snapshot.race_label && snapshot.race_label !== lastRace) {
					log("INFO", `field console is on ${snapshot.race_label}`);
					lastRace = snapshot.race_label;
				}
				return snapshot;
			},
		};
	}

	let liveDown = false;
	let lastRace = "";
	return {
		async read(url, capturedAt) {
			const base = url.replace(/\/+$/, "");
			const rt = await json<AthleticsRunningTime>(await get(`${base}${TRACK_TIMER_RUNNING_TIME_PATH}`, "results console"), "results console");
			let live: AthleticsLiveFeed | null = null;
			try {
				live = await json<AthleticsLiveFeed>(await get(`${base}${TRACK_TIMER_LIVE_PATH}`, "live feed"), "live feed");
				if (liveDown) log("INFO", "live feed answering again");
				liveDown = false;
			} catch (err) {
				if (!(err instanceof TimerUnavailable)) throw err;
				if (!liveDown) log("WARN", `${err.message} — relaying the results console without reactions`);
				liveDown = true;
			}
			const snapshot = athleticsToBenchSnapshot(rt, live, capturedAt);
			if (snapshot.race_label && snapshot.race_label !== lastRace) {
				log("INFO", `timer is on ${snapshot.race_label}`);
				lastRace = snapshot.race_label;
			}
			return snapshot;
		},
	};
}
